import React, { useState, useMemo } from 'react';
import { Box, Typography, ToggleButtonGroup, ToggleButton, IconButton, Alert, CircularProgress, Button } from '@mui/material';
import { RefreshRounded } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useCampaignPerformanceTrends } from '../../hooks/useCampaignPerformanceTrends';
import type { EngagementTimeRange } from '../../utils/engagementAnalytics';
import theme from '../../utils/theme';

type TimeRange = '7d' | '30d' | '90d' | 'all';

interface CampaignEngagementTrendsProps {
  campaignId: string;
}

/**
 * Format a date string to a readable format (MM/DD)
 */
const formatDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  } catch {
    return dateStr;
  }
};

/**
 * CampaignEngagementTrends Component
 * 
 * Displays a line chart showing daily engagement metrics (delivery rate, open rate, click rate)
 * over time for a single campaign with time range selection.
 */
const CampaignEngagementTrends: React.FC<CampaignEngagementTrendsProps> = ({ campaignId }) => {
  const [timeRange, setTimeRange] = useState<TimeRange>('all');
  
  /**
   * Convert UI timeRange to EngagementTimeRange for the hook
   * Note: 'all' is not supported by EngagementTimeRange type, so we use '180d' as the longest range
   */
  const getHookTimeRange = (): EngagementTimeRange => {
    if (timeRange === 'all') {
      return '180d';
    }
    return timeRange as EngagementTimeRange;
  };
  
  const { data, isLoading, isError, refetch } = useCampaignPerformanceTrends(
    campaignId,
    getHookTimeRange()
  );
  
  const handleTimeRangeChange = (_event: React.MouseEvent<HTMLElement>, newTimeRange: TimeRange | null) => {
    if (newTimeRange !== null) {
      setTimeRange(newTimeRange);
    }
  };
  
  const handleRefresh = () => {
    refetch();
  };
  
  // Format data for chart with memoization for performance
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    
    return data.map(day => ({
      date: formatDate(day.date),
      deliveryRate: day.sent > 0 ? Number(((day.delivered / day.sent) * 100).toFixed(1)) : 0,
      openRate: day.delivered > 0 ? Number(((day.opened / day.delivered) * 100).toFixed(1)) : 0,
      clickRate: day.delivered > 0 ? Number(((day.clicked / day.delivered) * 100).toFixed(1)) : 0,
    }));
  }, [data]);
  
  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 2,
        gap: { xs: 1, sm: 0 }
      }}>
        <Typography variant="h6" sx={{ mb: { xs: 1, sm: 0 } }}>Engagement Trends</Typography>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          width: { xs: '100%', sm: 'auto' },
          gap: 1
        }}>
          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={handleTimeRangeChange}
            size="small"
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              '& .MuiToggleButtonGroup-grouped': {
                flex: { xs: 1, sm: 'initial' },
                '&.MuiToggleButton-root': {
                  flex: { xs: 1, sm: 'initial' }
                }
              }
            }}
          >
            <ToggleButton value="7d">7d</ToggleButton>
            <ToggleButton value="30d">30d</ToggleButton>
            <ToggleButton value="90d">90d</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={handleRefresh} size="small" aria-label="refresh" sx={{ flexShrink: 0 }}>
            <RefreshRounded />
          </IconButton>
        </Box>
      </Box>
      
      {isError && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          Failed to load engagement trends. Please try again.
        </Alert>
      )}
      
      <Box sx={{ height: { xs: 250, sm: 300, md: 350 }, width: '100%' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : isError ? null : chartData.length === 0 ? (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              p: 3,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper'
            }}
          >
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No engagement data available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This campaign hasn't received any engagement yet or was recently created.
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis unit="%" />
              <Tooltip formatter={(value) => [`${value}%`, '']} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="deliveryRate" 
                name="Delivery Rate" 
                stroke={theme.palette.success.main} 
                activeDot={{ r: 8 }} 
              />
              <Line 
                type="monotone" 
                dataKey="openRate" 
                name="Open Rate" 
                stroke={theme.palette.info.main} 
              />
              <Line 
                type="monotone" 
                dataKey="clickRate" 
                name="Click Rate" 
                stroke={theme.palette.warning.main} 
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};

export default CampaignEngagementTrends;
